import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Spinner } from '@chakra-ui/react';

import { Txt } from '@app/components/common';
import { useAlert } from '@app/hooks/use-alert';

import { MasterOutletFormInstance } from './types';
import { ApplyDistributorsSection } from './components/apply-changes/apply-distributors-section';
import { ChangedFieldsSection } from './components/apply-changes/changed-fields-section';
import { OutletInfoSection } from './components/apply-changes/outlet-info-section';
import { SalesSourcesSection } from './components/apply-changes/sales-sources-section';
import { useSalesSourcesState } from './hooks/use-sales-sources-state';
import { useOutletDistributorsState } from './hooks/use-outlet-distributors-state';
import {
  buildAppliedInstance,
  buildInitialSelection,
  ChangeFieldKey,
  DefaultSelection,
  SelectedFields,
  UpdateDistributor,
  getApplyDistributorRows,
  getChangedFieldKeys,
  getSelectableDistributorIds,
} from './utils/apply-helpers';

interface OutletFormProps {
  instance: Partial<MasterOutletFormInstance>;
  origin?: Partial<MasterOutletFormInstance>;
  updateDistributors?: UpdateDistributor[];
  submitText?: string;
  submitAndSendText?: string;
  onSubmit?: (instance: Partial<MasterOutletFormInstance>) => void;
  onSubmitAndSend?: (instance: Partial<MasterOutletFormInstance>) => void;
  onCancel: () => void;
  onDeclineForm?: () => Promise<void>;
  /** Tweaks: показывать текущие значения в блоке изменённых полей */
  showCurrentValues?: boolean;
  /** Tweaks: стартовый выбор чекбоксов */
  defaultSelection?: DefaultSelection;
  readOnly?: boolean;
}

const OutletForm: FC<OutletFormProps> = (props) => {
  const { warning } = useAlert();
  const {
    instance,
    origin,
    updateDistributors,
    onSubmit,
    onDeclineForm,
    showCurrentValues = true,
    defaultSelection = 'all',
    readOnly = false,
  } = props;

  console.log('instance', instance);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    salesSources: outletSalesSources,
    isLoading: isLoadingSalesSources,
  } = useSalesSourcesState(instance.id);

  const {
    distributors: outletDistributors,
    distributorNames,
    isLoading: isLoadingOutletDistributors,
    applyDistributorChanges,
  } = useOutletDistributorsState({
    outlet_id: instance.id,
    deferChanges: true,
  });

  const changedFields = useMemo(
    () => getChangedFieldKeys(origin, instance),
    [origin, instance]
  );

  const distributorRows = useMemo(
    () => getApplyDistributorRows(outletDistributors, updateDistributors, distributorNames),
    [distributorNames, outletDistributors, updateDistributors]
  );

  const selectableDistributorIds = useMemo(
    () => getSelectableDistributorIds(distributorRows),
    [distributorRows]
  );

  const selectionKey = useMemo(
    () => `${changedFields.join(',')}|${selectableDistributorIds.join(',')}|${defaultSelection}`,
    [changedFields, selectableDistributorIds, defaultSelection]
  );

  const [selectedFields, setSelectedFields] = useState<SelectedFields>({});
  const [selectedDistributors, setSelectedDistributors] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const initial = buildInitialSelection(changedFields, selectableDistributorIds, defaultSelection);
    setSelectedFields(initial.fields);
    setSelectedDistributors(initial.distributors);
  }, [selectionKey, changedFields, selectableDistributorIds, defaultSelection]);

  const selectableKeys = useMemo(() => {
    const keys: Array<ChangeFieldKey | `distributor:${number}`> = [...changedFields];
    selectableDistributorIds.forEach((id) => keys.push(`distributor:${id}`));
    return keys;
  }, [changedFields, selectableDistributorIds]);

  const selectedCount = useMemo(() => {
    let count = 0;
    changedFields.forEach((key) => {
      if (selectedFields[key]) {
        count += 1;
      }
    });
    selectableDistributorIds.forEach((id) => {
      if (selectedDistributors[id]) {
        count += 1;
      }
    });
    return count;
  }, [changedFields, selectableDistributorIds, selectedFields, selectedDistributors]);

  const allSelected = selectableKeys.length > 0 && selectedCount === selectableKeys.length;

  const toggleField = useCallback((key: ChangeFieldKey) => {
    setSelectedFields((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleDistributor = useCallback((distributorId: number) => {
    setSelectedDistributors((prev) => ({
      ...prev,
      [distributorId]: !prev[distributorId],
    }));
  }, []);

  const toggleAll = useCallback(() => {
    const nextValue = !allSelected;
    setSelectedFields((prev) => {
      const next = { ...prev };
      changedFields.forEach((key) => {
        next[key] = nextValue;
      });
      return next;
    });
    setSelectedDistributors((prev) => {
      const next = { ...prev };
      selectableDistributorIds.forEach((id) => {
        next[id] = nextValue;
      });
      return next;
    });
  }, [allSelected, changedFields, selectableDistributorIds]);

  const onApply = useCallback(async () => {
    if (!onSubmit || !origin) {
      return;
    }

    setIsSubmitting(true);

    try {
      const shouldApplyDistributors = selectableDistributorIds.some(
        (id) => selectedDistributors[id]
      );

      if (shouldApplyDistributors) {
        const selectedDistributorIds = selectableDistributorIds.filter(
          (id) => selectedDistributors[id]
        );
        const isApplied = await applyDistributorChanges(
          updateDistributors || [],
          selectedDistributorIds
        );
        if (!isApplied) {
          warning('Не удалось применить изменения дистрибьюторов');
          return;
        }
      }

      const result = buildAppliedInstance(
        origin,
        instance,
        selectedFields,
        false
      );
      onSubmit(result);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    applyDistributorChanges,
    instance,
    onSubmit,
    origin,
    selectableDistributorIds,
    selectedDistributors,
    selectedFields,
    updateDistributors,
    warning,
  ]);

  const onDecline = useCallback(async () => {
    if (!onDeclineForm) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onDeclineForm();
    } finally {
      setIsSubmitting(false);
    }
  }, [onDeclineForm]);

  const isLoading = isLoadingSalesSources || isLoadingOutletDistributors;

  if (isLoading && !outletSalesSources.length && !outletDistributors.length) {
    return (
      <Box
        width="100%"
        height="300px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner />
      </Box>
    );
  }

  const displayOutlet = origin || instance;

  return (
    <Box>
      {instance.outlet_acb_id && (
        <Box fontSize="15px" color="#3a4150" mb={5} lineHeight={1.5}>
          <Txt as="span">Создана по заявке </Txt>
          <Txt as="span" color="brand.500">#{instance.outlet_acb_id}</Txt>
          <Txt as="span"> от </Txt>
          <Txt as="span" color="brand.500">
            {instance.acb_user?.full_name} ({instance.acb_user?.username})
          </Txt>
          <Txt as="span"> дата создания </Txt>
          <Txt as="span" color="brand.500">
            {moment(instance.create_dt).format('DD.MM.YYYY HH:mm')}
          </Txt>
        </Box>
      )}

      <OutletInfoSection outlet={displayOutlet} />

      <SalesSourcesSection
        salesSources={outletSalesSources}
        isLoading={isLoadingSalesSources}
      />

      <ApplyDistributorsSection
        rows={distributorRows}
        isLoading={isLoadingOutletDistributors}
        hasChanges={selectableDistributorIds.length > 0}
        selected={selectedDistributors}
        onToggle={toggleDistributor}
        readOnly={readOnly}
      />

      <ChangedFieldsSection
        origin={origin}
        instance={instance}
        changedFields={changedFields}
        selectedFields={selectedFields}
        showCurrentValues={showCurrentValues}
        selectedCount={selectedCount}
        totalCount={selectableKeys.length}
        allSelected={allSelected}
        isSubmitting={isSubmitting}
        onToggleField={toggleField}
        onToggleAll={toggleAll}
        onApply={onApply}
        onDecline={onDeclineForm ? onDecline : undefined}
        readOnly={readOnly}
      />
    </Box>
  );
};

export default OutletForm;
